import { AxiosError } from 'axios'
import React, { ChangeEvent, FormEvent, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { setToast } from '@/entities/toast'
import { setUserSession } from '@/entities/user/sessionSlice'
import { postApiAuthLogin, usePostApiAuthRegister } from '@/shared/apis/fastmile/generated/auth/auth'
import { ClientRegisterDto } from '@/shared/apis/fastmile/models'
import { ApiErrorResponse } from '@/shared/apis/fastmile/models/ApiErrorResponse'
import { Icon } from '@/shared/components/Icon/Icon'
import { TextField } from '@/shared/components/TextField/TextField'
import { useAppDispatch } from '@/shared/store'
import { faChartLine, faClipboard, faClock, faMap, faUsers } from '@fortawesome/pro-regular-svg-icons'
import { Box, FormControl, FormLabel, Typography, useTheme } from '@mui/material'

import {
  StyledAlert, StyledContainer, StyledFeatureDescription, StyledFeatureIcon, StyledFeatureItem, StyledFeatureList, StyledFeatureText,
  StyledFeatureTitle, StyledForm, StyledLeftSection, StyledLink, StyledLogo, StyledRegistration, StyledRightSection, StyledSubmitButton,
  StyledTermsText, StyledTextFieldLabel,
} from './StyledRegistration'

export type RegistrationProps = {
  //
}

const features = [
  {
    icon: <Icon icon={faClock} />,
    title: 'Smart Planning',
    description: 'AI-powered shift planning and route optimization that considers all your operational constraints and requirements.',
  },
  {
    icon: <Icon icon={faMap} />,
    title: 'Live Tracking',
    description: 'Real-time fleet tracking with integrated telematics for complete visibility of your operations.',
  },
  {
    icon: <Icon icon={faClipboard} />,
    title: 'Digital Runsheets',
    description: 'Convert planned scenarios into digital runsheets for seamless driver communication and job tracking.',
  },
  {
    icon: <Icon icon={faUsers} />,
    title: 'Driver App',
    description: 'Mobile app for drivers with navigation, job management, and digital proof of delivery.',
  },
  {
    icon: <Icon icon={faChartLine} />,
    title: 'Analytics',
    description: 'Comprehensive reporting and analytics to identify optimization opportunities and track KPIs.',
  },
]

export const Registration: React.FC<RegistrationProps> = () => {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [registrationComplete, setRegistrationComplete] = useState(false)

  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const theme = useTheme()

  const mutation = usePostApiAuthRegister({
    mutation: {
      onSuccess: async () => {
        const loginResult = await postApiAuthLogin({
          login: email,
          password: password,
          imei: '',
          info: '',
          client: '',
          deviceId: '',
          installId: '',
        })

        if (loginResult?.token) {
          // Set the user session. The LoginRoute will automatically navigate after the user session is set
          dispatch(setUserSession({
            token: loginResult.token,
            accessToken: loginResult.accessToken || null,
            refreshToken: loginResult.refreshToken || null,
            isEmailVerified: loginResult.isEmailVerified || false,
            email: loginResult.userEmail || null,
          }))

          // Show success message
          dispatch(setToast({
            open: true,
            severity: 'success',
            title: 'Registration Successful',
            content: 'Your account has been created and you are now logged in.',
          }))
        } else {
          setRegistrationComplete(true)
          dispatch(setToast({
            open: true,
            severity: 'info',
            title: 'Registration Successful',
            content: 'Your account has been created. Please verify your email and login.',
          }))
        }

        dispatch(setToast({
          open: true,
          severity: 'success',
          title: 'Registration Successful',
          content: 'Your account has been created. Please verify your email to continue.',
        }))
        setRegistrationComplete(true)
      },
      onError: (err: AxiosError<ApiErrorResponse>) => {
        console.log('DEBUG error: ', err)
        setError(`Registration failed: ${err.response?.data?.detail}`)
        console.error('Registration error:', err)
      },
    },
  })

  const onRegister = async () => {
    // Basic validation
    if (!name || !email || !password) {
      setError('Please fill in all required fields')

      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')

      return
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long')

      return
    }

    setError(null)

    const clientRegisterModel: ClientRegisterDto = {
      name,
      email,
      password,
    }

    mutation.mutate({ data: clientRegisterModel })
  }

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    onRegister()
  }

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value)
  }

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value)
  }

  const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value)
  }

  const handleConfirmPasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(event.target.value)
  }

  const handleLoginRedirect = () => {
    navigate('/')
  }

  return (
    <StyledRegistration>
      <StyledContainer>
        <StyledLeftSection>
          <StyledLogo src={theme.palette.mode === 'light' ? '/login-logo-dark.svg' : '/login-logo-light.svg'} alt="Fastmile Logo" />
          <StyledFeatureList>
            {features.map((feature, index) => (
              <StyledFeatureItem key={index}>
                <StyledFeatureIcon>
                  {feature.icon}
                </StyledFeatureIcon>
                <StyledFeatureText>
                  <StyledFeatureTitle variant="subtitle1">
                    {feature.title}
                  </StyledFeatureTitle>
                  <StyledFeatureDescription variant="body2">
                    {feature.description}
                  </StyledFeatureDescription>
                </StyledFeatureText>
              </StyledFeatureItem>
            ))}
          </StyledFeatureList>
        </StyledLeftSection>

        <StyledRightSection>
          {registrationComplete ? (
            <>
              <Typography variant="h4" component="h1" align="center" gutterBottom>
                Verification Email Sent
              </Typography>
              <Typography variant="body1" align="center" sx={{ mb: 3 }}>
                A verification email has been sent to <strong>{email}</strong>.
              </Typography>
              <Typography variant="body2" color="textSecondary" align="center" sx={{ mb: 3 }}>
                Please verify your email address to continue using Fastmile.
              </Typography>
              <StyledAlert severity="info" sx={{ mb: 3 }}>
                If you don't see the email in your inbox, please check your spam or junk folder.
              </StyledAlert>
              <StyledSubmitButton
                fullWidth
                primary
                size="medium"
                onClick={handleLoginRedirect}
              >
                Go to Login
              </StyledSubmitButton>
            </>
          ) : (
            <>
              <Typography variant="h4" component="h1" align="center" gutterBottom>
                Create an account
              </Typography>
              <Typography variant="body2" color="textSecondary" align="center" sx={{ mb: 3 }}>
                Sign up to get started with Fastmile
              </Typography>

              {error && (
                <StyledAlert severity="error">
                  {error}
                </StyledAlert>
              )}

              <StyledForm onSubmit={onSubmit}>
                <FormControl required>
                  <FormLabel htmlFor="name">
                    Organisation Name
                  </FormLabel>
                  <TextField
                    id="name"
                    type="text"
                    autoFocus
                    autoComplete="name"
                    aria-describedby="name"
                    onChange={handleNameChange}
                    fullWidth
                  />
                </FormControl>

                <FormControl required>
                  <StyledTextFieldLabel htmlFor="email">
                    Email
                  </StyledTextFieldLabel>
                  <TextField
                    id="email"
                    type="email"
                    autoComplete="email"
                    aria-describedby="email"
                    onChange={handleEmailChange}
                    fullWidth
                  />
                </FormControl>

                <FormControl required>
                  <StyledTextFieldLabel htmlFor="password">
                    Password
                  </StyledTextFieldLabel>
                  <TextField
                    id="password"
                    type="password"
                    autoComplete="new-password"
                    aria-describedby="password"
                    onChange={handlePasswordChange}
                    fullWidth
                  />
                </FormControl>

                <FormControl required>
                  <StyledTextFieldLabel htmlFor="confirmPassword">
                    Confirm Password
                  </StyledTextFieldLabel>
                  <TextField
                    id="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    aria-describedby="confirmPassword"
                    onChange={handleConfirmPasswordChange}
                    fullWidth
                  />
                </FormControl>

                <StyledSubmitButton
                  fullWidth
                  primary
                  size="medium"
                  type="submit"
                  disabled={mutation.isPending}
                >
                  Create Account
                </StyledSubmitButton>

                <StyledTermsText>
                  By signing up you agree to our Terms of Service and Privacy Policy
                </StyledTermsText>
              </StyledForm>

              <Box mt={2} textAlign="center">
                <Typography variant="body2">
                  Already have an account?{' '}
                  <StyledLink to="/">
                    Log in
                  </StyledLink>
                </Typography>
              </Box>
            </>
          )}
        </StyledRightSection>
      </StyledContainer>
    </StyledRegistration>
  )
}